/* eslint-disable max-lines -- Login page has multiple inline form states (magic link, password, forgot password, confirmations) */
import { Box, Button, Link, TextField, ThemeProvider } from "@mui/material";
import { ensureCognitoUser } from "@src/appV2/Auth/cognito/cognitoAuth";
import { requestPasswordReset } from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { initCognitoSession } from "@src/appV2/Auth/cognito/initCognitoSession";
import { isSafeRedirectUrl } from "@src/appV2/Auth/cognito/isSafeRedirectUrl";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import { loginWithEmailAndPassword } from "@src/mobile/Login/loginWithEmailAndPassword";
import { sendLoginEmail } from "@src/mobile/Login/sendLoginEmail";
import { type ReactElement, useCallback, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk after password login
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import clipboardWordmark from "../../../../public/assets/clipboard-wordmark-brown.png";
import loginIllustration from "./assets/login-illustration.webp";
import { LoginForm } from "./LoginForm";
import { LoginWithEmailAndPasswordForm } from "./LoginWithEmailAndPasswordForm";
import { useSsoLogin } from "./useSsoLogin";

const WORKPLACE_SIGNUP_URL = "https://www.clipboardworks.com/workplaces/signup";

type LoginMode = "magicLink" | "password" | "forgotPassword" | "resetEmailSent";

const VALID_LOGIN_MODES: readonly LoginMode[] = [
  "magicLink",
  "password",
  "forgotPassword",
  "resetEmailSent",
];

function parseInitialLoginMode(search: string): LoginMode {
  const mode = new URLSearchParams(search).get("mode");
  return VALID_LOGIN_MODES.find((m) => m === mode) ?? "magicLink";
}

export function Login(): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [loginMode, setLoginMode] = useState<LoginMode>(() =>
    parseInitialLoginMode(location.search)
  );
  const [loginError, setLoginError] = useState<string | undefined>();
  const [resetError, setResetError] = useState<string | undefined>();

  const { ssoMode, handleSsoRedirect } = useSsoLogin(email);

  const handleMagicLinkSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    setLoading(true);

    // "on" mode: SSO replaces magic link for CBH employees
    if (ssoMode === "on") {
      await handleSsoRedirect("submit");
      setLoading(false);
      return;
    }

    const success = await sendLoginEmail({
      email,
      requestedURL: localStorage.getItem("redirectURL") ?? undefined,
    });

    if (success) {
      setEmailSent(true);
    }

    setLoading(false);
  }, [email, ssoMode, handleSsoRedirect]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setLoading(true);
    setLoginError(undefined);

    const result = await loginWithEmailAndPassword({ email, password });

    if (result.success) {
      // Hydrate Redux session so PrivateRoute sees a valid session immediately,
      // matching what AuthRedirect does for the magic link flow.
      const sessionRestored = await dispatch(initCognitoSession());
      if (!sessionRestored) {
        setLoginError("Something went wrong. Please try again.");
        setLoading(false);
        return;
      }

      const storedRedirectUrl = localStorage.getItem("redirectURL");
      const redirectUrl =
        storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
      localStorage.removeItem("redirectURL");
      history.replace(redirectUrl);
      return;
    }

    setLoginError(result.error);
    setLoading(false);
  }, [email, password, dispatch, history]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) {
      setResetError("Please enter your email address first.");
      return;
    }

    setLoading(true);
    setResetError(undefined);

    try {
      await ensureCognitoUser(email);
      await requestPasswordReset(email);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUESTED);
      setLoginMode("resetEmailSent");
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "password");
      logError(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUEST_FAILURE, {
        error,
        metadata: { errorCode: mapped.code },
      });
      setResetError(mapped.message);
    } finally {
      setLoading(false);
    }
  }, [email]);

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.baseDark",
          overflow: "auto",
          paddingX: 5,
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Wordmark */}
        <img
          src={clipboardWordmark}
          alt="Clipboard"
          style={{ height: 70, width: "auto", marginBottom: 40 }}
        />

        {/* Illustration - hidden on confirmation states */}
        {!(loginMode === "magicLink" && emailSent) && loginMode !== "resetEmailSent" && (
          <img
            src={loginIllustration}
            alt="Two people shaking hands"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}

        {/* Form */}
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {loginMode === "magicLink" && !emailSent && (
            <LoginForm
              email={email}
              isSsoEmail={ssoMode === "on"}
              loading={loading}
              showSsoButton={ssoMode === "test"}
              setEmail={setEmail}
              onSsoClick={() => handleSsoRedirect("sso_button")}
              onSubmit={handleMagicLinkSubmit}
              onSwitchToPassword={() => {
                setLoginMode("password");
              }}
            />
          )}

          {loginMode === "magicLink" && emailSent && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
                <Text bold variant="h5" sx={{ color: "base.base" }}>
                  Verification link sent
                </Text>
              </Box>
              <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
                We have sent an email with a login link to {email}.
              </Text>
              <Button
                variant="text"
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "primary.main",
                }}
                onClick={() => {
                  setEmailSent(false);
                }}
              >
                Send another email
              </Button>
            </Box>
          )}

          {loginMode === "password" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <LoginWithEmailAndPasswordForm
                email={email}
                password={password}
                loading={loading}
                errorMessage={loginError}
                setEmail={setEmail}
                setPassword={setPassword}
                onSubmit={handlePasswordSubmit}
                onForgotPassword={() => {
                  setLoginError(undefined);
                  setLoginMode("forgotPassword");
                }}
              />
              {/* Divider */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  alignSelf: "stretch",
                  gap: 5,
                  marginTop: 4,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "border.base",
                  }}
                />
                <Text variant="body2" sx={{ color: "base.inactive" }}>
                  or
                </Text>
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    backgroundColor: "border.base",
                  }}
                />
              </Box>

              <Button
                variant="text"
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "primary.main",
                  marginTop: 4,
                  alignSelf: "center",
                }}
                onClick={() => {
                  setLoginMode("magicLink");
                }}
              >
                Sign in with magic link
              </Button>
            </Box>
          )}

          {loginMode === "forgotPassword" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 4,
                width: "100%",
              }}
            >
              <Text bold variant="body1" sx={{ textAlign: "center" }}>
                Reset your password
              </Text>
              <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
                Enter your email address and we'll send you a link to reset your password.
              </Text>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                placeholder="your.email@example.com"
                value={email}
                disabled={loading}
                autoComplete="email"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1rem",
                    borderRadius: (theme) => theme.borderRadius?.xLarge,
                    backgroundColor: "background.standout",
                  },
                }}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
              {resetError && (
                <Text
                  variant="body2"
                  role="alert"
                  sx={{ color: "error.main", textAlign: "center" }}
                >
                  {resetError}
                </Text>
              )}
              <LoadingButton
                fullWidth
                variant="primary"
                size="large"
                isLoading={loading}
                disabled={!email}
                onClick={() => {
                  void handleForgotPassword();
                }}
              >
                Send Reset Link
              </LoadingButton>
              <Button
                variant="text"
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "primary.main",
                  alignSelf: "center",
                }}
                onClick={() => {
                  setResetError(undefined);
                  setLoginMode("password");
                }}
              >
                Back to sign in
              </Button>
            </Box>
          )}

          {loginMode === "resetEmailSent" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
                <Text bold variant="h5" sx={{ color: "base.base" }}>
                  Reset link sent
                </Text>
              </Box>
              <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
                We have sent a password reset link to {email}. Check your email and follow the
                instructions.
              </Text>
              <Button
                variant="text"
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "primary.main",
                }}
                onClick={() => {
                  setLoginMode("password");
                }}
              >
                Back to sign in
              </Button>
            </Box>
          )}

          <Box sx={{ marginTop: 6, display: "flex", justifyContent: "center", gap: 1 }}>
            <Text variant="body2" sx={{ color: "base.muted" }}>
              Don&apos;t have a workplace account?
            </Text>
            <Link
              href={WORKPLACE_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontSize: "0.875rem", color: "primary.main" }}
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.LOGIN_SIGNUP_LINK_CLICKED);
              }}
            >
              Sign up
            </Link>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
